import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Clock } from "@phosphor-icons/react";

const ACTIVITY_KEY = "equipr:last-activity";
const IDLE_FLAG = "equipr:idle-logout";
const EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];

/* Inactivity auto-logout for the main office app. Mounted inside ProtectedRoute,
   so the field/PWA kiosk routes (sign-in, docket, timesheet) are never affected. */
export default function IdleTimeout() {
  const { user, logout } = useAuth();
  const authed = !!user;
  const [warnLeft, setWarnLeft] = useState(null); // null hidden; else seconds remaining
  const lastRef = useRef(Date.now());
  const warnRef = useRef(false);
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  const stayActive = useCallback(() => {
    lastRef.current = Date.now();
    warnRef.current = false;
    setWarnLeft(null);
    try { localStorage.setItem(ACTIVITY_KEY, String(lastRef.current)); } catch { /* ignore */ }
  }, []);

  const doLogout = useCallback(() => {
    try { sessionStorage.setItem(IDLE_FLAG, "1"); localStorage.removeItem(ACTIVITY_KEY); } catch { /* ignore */ }
    logoutRef.current();
  }, []);

  useEffect(() => {
    if (!authed) return undefined;
    const IDLE_MS = Number(window.__EQUIPR_IDLE_MS__) || 60 * 60 * 1000;   // 60 min
    const WARN_MS = Number(window.__EQUIPR_WARN_MS__) || 60 * 1000;        // 60 s warning
    lastRef.current = Date.now();

    let lastWrite = 0;
    const onActivity = () => {
      const now = Date.now();
      lastRef.current = now;
      if (warnRef.current) { warnRef.current = false; setWarnLeft(null); }
      if (now - lastWrite > 2000) { lastWrite = now; try { localStorage.setItem(ACTIVITY_KEY, String(now)); } catch { /* ignore */ } }
    };
    const onStorage = (e) => {
      if (e.key === ACTIVITY_KEY && e.newValue) {
        const t = Number(e.newValue);
        if (t > lastRef.current) { lastRef.current = t; if (warnRef.current) { warnRef.current = false; setWarnLeft(null); } }
      }
    };
    EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    window.addEventListener("storage", onStorage);

    const id = setInterval(() => {
      const idle = Date.now() - lastRef.current;
      if (idle >= IDLE_MS) {
        doLogout();
      } else if (idle >= IDLE_MS - WARN_MS) {
        warnRef.current = true;
        setWarnLeft(Math.max(0, Math.ceil((IDLE_MS - idle) / 1000)));
      }
    }, 1000);

    return () => {
      EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
      window.removeEventListener("storage", onStorage);
      clearInterval(id);
    };
  }, [authed, doLogout]);

  if (!authed || warnLeft == null) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-[rgba(26,26,26,0.42)] flex items-center justify-center p-4" data-testid="idle-warning">
      <div className="bg-surface-panel border border-border-warm rounded-[14px] shadow-modal w-full max-w-sm p-5">
        <div className="flex items-center gap-2 text-ink-primary">
          <Clock size={20} weight="bold" className="text-accent-ink" />
          <h3 className="font-bold text-[16px]">Are you still there?</h3>
        </div>
        <p className="text-[13px] text-ink-secondary mt-2 leading-relaxed">
          You&rsquo;ve been inactive for a while. For your security you&rsquo;ll be signed out in{" "}
          <b className="text-ink-primary tabular-nums" data-testid="idle-countdown">{warnLeft}s</b>.
        </p>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={doLogout} data-testid="idle-logout-now"
            className="h-9 px-4 border border-border-control bg-white text-ink-primary text-[12px] uppercase tracking-[0.04em] font-semibold rounded-[8px] hover:bg-surface-panel active:bg-surface-pressed">
            Log out now
          </button>
          <button onClick={stayActive} data-testid="idle-stay"
            className="h-9 px-5 bg-equipr-yellow text-black text-[12px] uppercase tracking-[0.04em] font-bold rounded-[8px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px">
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
}
